import { MessageSourceProps, withMessages } from "react-message-source";
import { TariffDto, TariffTaxPointDto, UpsertTariffTaxPointDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { noop } from 'utils/functionUtils';
import { FormState } from 'final-form';
import { deleteTariffTaxPoint, findTariffTaxPointById, getAllTariffTaxPoints } from 'services/tariffTaxPointServices';
import { Grid, Modal } from 'semantic-ui-react';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { getAllTariffs } from 'services/tariffServices';
import Select from 'components/final-form/Select';
import UpsertTariffTaxPointForm from 'components/tariffs/UpsertTariffTaxPointForm';
import DeleteActionButton from 'components/DeleteActionButton';
import LoaderComponent from 'components/LoaderComponent';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import moment from 'moment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

interface Props extends MessageSourceProps {
  activeTariff: TariffDto
}

interface State {
  foundTariffTaxPoints: Array<TariffTaxPointDto>,
  tariffs: Array<TariffDto>,
  activeTariff: number,
  cancelTokenSource: CancelTokenSource,
  tariffTaxPointsLoaded: boolean,
  visibleTariffTaxPoints: Array<TariffTaxPointDto>,
  addEditModalOpen: boolean,
  tariffTaxPoint?: UpsertTariffTaxPointDto,
  tariffTaxPointId?: number
}

interface TariffTaxPointFieldProps extends FormState {
  tariffId?: number
}

class TariffTaxPointView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTariffTaxPoints: [],
      tariffTaxPointsLoaded: false,
      addEditModalOpen: false,
      tariffs: [],
      activeTariff: -1,
      visibleTariffTaxPoints: [],
      cancelTokenSource
    };

    const onFinally = () => {
      this.setState({
        tariffTaxPointsLoaded: true
      })
    };

    getAllTariffs(cancelTokenSource)
        .then(tariffs => {
          getAllTariffTaxPoints(cancelTokenSource)
              .then(tariffTaxPoints => {
                this.setState({
                  foundTariffTaxPoints: tariffTaxPoints,
                  tariffs: tariffs
                });
              })
              .catch(noop)
              .finally(onFinally);
        }).catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  tariffChange = ({values}: TariffTaxPointFieldProps): void => {

    if (!this.mounted) {
      return;
    }

    const {foundTariffTaxPoints} = this.state;

    let visibleTariffTaxPoints = foundTariffTaxPoints.filter(x => x.tariffId === values.tariffId);
    this.setState({
      visibleTariffTaxPoints: visibleTariffTaxPoints,
      activeTariff: values.tariffId
    })

  };

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      let methods: Array<any> = [findTariffTaxPointById(id, cancelTokenSource), getAllTariffs(cancelTokenSource)];
      Promise.all(methods)
          .then(responses => {
            this.showModal(id, responses[0], responses[1]);
          })
          .catch(noop);
    }
  };

  showModal = (id?: number, tariffTaxPoint?: UpsertTariffTaxPointDto, tariffs?: Array<TariffDto>) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        tariffTaxPoint: tariffTaxPoint,
        tariffTaxPointId: id,
        tariffs: tariffs ? tariffs : this.state.tariffs
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      tariffTaxPointId: undefined,
      tariffTaxPoint: undefined
    }));
  };

  render(): React.ReactNode {
    const {tariffTaxPointsLoaded, addEditModalOpen} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderFilterTariffTaxPointsSegment()}
          </div>
          <StyledViewTableRow>
            {!tariffTaxPointsLoaded
                ? <LoaderComponent message={getMessage("tariffTaxPoint.loading")}/>
                : this.renderTariffTaxPointSegment()}
          </StyledViewTableRow>
          {addEditModalOpen && this.renderUpsertTariffTaxPointModal()}
        </PraxStyledSegment>
    );
  }

  renderUpsertTariffTaxPointModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, tariffTaxPoint, tariffTaxPointId, activeTariff, tariffs} = this.state;

    let activeTariffObject = tariffs.filter(x => x.id === activeTariff)[0];

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{`${getMessage("tariffTaxPoint.value")} ${activeTariffObject.description}`}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTariffTaxPointForm
                tariffTaxPoint={tariffTaxPoint}
                tariffTaxPointId={tariffTaxPointId}
                activeTariff={activeTariffObject}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  };

  onSaveUpsertForm = () => {
    const {cancelTokenSource, activeTariff} = this.state;

    const onFinally = () => {
      this.setState({
        tariffTaxPointsLoaded: true
      })
    };

    getAllTariffTaxPoints(cancelTokenSource)
        .then(response => {
          let visibleTariffTaxPoints = response.filter(x => x.tariffId === activeTariff);
          this.setState({
            foundTariffTaxPoints: response,
            visibleTariffTaxPoints: visibleTariffTaxPoints
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  renderTariffTaxPointSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {visibleTariffTaxPoints} = this.state;

    return (
        <VirtualizedTable
            rowCount={visibleTariffTaxPoints.length}
            rowGetter={this.tariffTaxPointRowGetter}
            rowRenderer={this.foundTariffTaxPointsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("tariffTaxPoint.fromDate")),
                dataKey: "fromDate",
                cellRenderer: (this.dateCellRenderer)
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("tariffTaxPoint.defaultPoints")),
                dataKey: "defaultPoints"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  dateCellRenderer = ({cellData}: any) => (<div>{moment(cellData).format('DD.MM.YYYY')}</div>);

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("tariffTaxPoint.confirmDelete")}
              onConfirm={() => this.deleteTariffTaxPoint(cellData)}
          />
        </React.Fragment>
    );
  };

  tariffTaxPointRowGetter = ({index}: any) => {
    const {visibleTariffTaxPoints} = this.state;

    Object.assign(visibleTariffTaxPoints[index], {index: index + 1});

    return visibleTariffTaxPoints[index]
  };

  foundTariffTaxPointsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {visibleTariffTaxPoints} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(visibleTariffTaxPoints[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderFilterTariffTaxPointsSegment = (): JSX.Element => {
    const {tariffs} = this.state;

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffId: tariffs && tariffs.length ?
                  tariffs[0].id : undefined
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {tariffs, visibleTariffTaxPoints} = this.state;

    const numResults = visibleTariffTaxPoints.length;
    const tariffCodeOptions = tariffs.map(tariffCode => {

      return {
        key: (tariffCode.id),
        text: tariffCode.description,
        value: tariffCode.id
      }
    });

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("tariffTaxPoint.tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffId"
                  component={Select}
                  fluid
                  options={tariffCodeOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.tariffChange}/>
        </PxGrid>
    );
  };

  deleteTariffTaxPoint = (id: number): void => {
    const {cancelTokenSource, visibleTariffTaxPoints} = this.state;
    deleteTariffTaxPoint(id, cancelTokenSource).then(response => {

      this.setState(({foundTariffTaxPoints}: State) => ({
        foundTariffTaxPoints: foundTariffTaxPoints.filter((tariffTaxPoint: TariffTaxPointDto) => tariffTaxPoint.id != id),
        visibleTariffTaxPoints: visibleTariffTaxPoints.filter((tariffTaxPoint: TariffTaxPointDto) => tariffTaxPoint.id != id)
      }))
    }).catch(noop);
  };


}

export default withMessages(TariffTaxPointView);
